/**
 * Authentication cache utilities for optimistic auth state
 * Improves UX by reducing authentication loading flashes
 */

import { User } from '../services/userApi'

const AUTH_CACHE_KEY = 'evop_auth_cache'
const CACHE_VALIDITY_MS = 5 * 60 * 1000 // 5 minutes

interface AuthCache {
  isAuthenticated: boolean
  user: User | null
  timestamp: number
}

/**
 * Store auth state in session storage
 */
export function setAuthCache(isAuthenticated: boolean, user?: User): void {
  try {
    const cache: AuthCache = {
      isAuthenticated,
      user: user || null,
      timestamp: Date.now()
    }
    sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cache))
  } catch (error) {
    console.warn('Failed to set auth cache:', error)
  }
}

/**
 * Retrieve auth state from cache if valid
 * Returns null if cache is expired or invalid
 */
export function getAuthCache(): AuthCache | null {
  try {
    const cached = sessionStorage.getItem(AUTH_CACHE_KEY)
    if (!cached) return null

    const cache: AuthCache = JSON.parse(cached)
    const age = Date.now() - cache.timestamp

    // Check if cache is still valid
    if (age > CACHE_VALIDITY_MS) {
      clearAuthCache()
      return null
    }

    return cache
  } catch (error) {
    console.warn('Failed to get auth cache:', error)
    clearAuthCache()
    return null
  }
}

/**
 * Clear auth cache (on logout or expiration)
 */
export function clearAuthCache(): void {
  try {
    sessionStorage.removeItem(AUTH_CACHE_KEY)
  } catch (error) {
    console.warn('Failed to clear auth cache:', error)
  }
}

/**
 * Check if we have a valid cached auth state
 */
export function hasValidAuthCache(): boolean {
  const cache = getAuthCache()
  return cache !== null && cache.isAuthenticated
}